package game

import engine.controller.defaults.DefaultGameEngine
import engine.model.GameState
import engine.model.KtrisContext
import engine.model.events.EventOrchestrator
import game.events.GravitarEvents

/**
 * Gravitar's engine layer on top of [DefaultGameEngine].
 *
 * Extended with:
 * - [clearBoard]         — zero every cell; used on reset/freeze wipe.
 * - [reducePlayingField] — abyss-mode rising floor.
 * - [setLockDelay]       — apply a card-driven lock-delay override in-place.
 * - [resetPieceState]    — discard the current piece and start the entry-delay
 *                          cycle, without ending the game. Used after a freeze
 *                          board-wipe so the next piece spawns cleanly.
 * - [swapBag]            — replace the bag randomizer; takes effect on next spawn.
 */
class GravitarEngine(private val ktrisContext: KtrisContext) : DefaultGameEngine(
    ktrisContext.playerSettings,
    ktrisContext.gameSettings,
    ktrisContext.boardManager,
    ktrisContext.pieceController,
    ktrisContext.scoringEngine,
) {
    override val gameId: String = ktrisContext.gameId

    // ── Board manipulation ────────────────────────────────────────────────────

    /**
     * Shifts every row up by one and fills the new bottom row with [fillId].
     * Used by abyss mode. Publishes [GravitarEvents.AbyssFloorRisen].
     */
    fun reducePlayingField(fillId: Int = -98) {
        val board = boardManager.board
        for (r in 0 until board.rows - 1)
            for (c in 0 until board.cols)
                board[r, c] = board[r + 1, c]
        for (c in 0 until board.cols)
            board[board.rows - 1, c] = fillId
        EventOrchestrator.publish(
            GravitarEvents.AbyssFloorRisen(
                newFloorRow = board.rows - 1,
                gameId = gameId,
            )
        )
    }

    /**
     * Sets every board cell to zero.
     * Call [resetPieceState] immediately after if you want piece spawning to
     * resume (freeze-mode topout recovery).
     */
    fun clearBoard() {
        val board = boardManager.board
        for (r in 0 until board.rows)
            for (c in 0 until board.cols)
                board[r, c] = 0
        EventOrchestrator.publish(GravitarEvents.FreezeBoardResumed(gameId = gameId))
    }

    fun resetPlayingState() {
        this.gameState = GameState.ENTRY_DELAY
    }

    // ── Convenience accessors ─────────────────────────────────────────────────

    val board get() = boardManager.board
    val isFrozen get() = timeManager.isFrozen
    val rotationState get() = pieceController.currentPiece?.rotationState
}
